import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, Lock, Wifi, Database, Server, CheckCircle2 } from 'lucide-react';
import '@/App.css';

const connectionSteps = [
  { icon: Wifi, text: 'Establishing encrypted tunnel to Meridian Global Zurich Production...', delay: 400 },
  { icon: Lock, text: 'Verifying identity credentials...', delay: 900 },
  { icon: Database, text: 'Loading Temporal AI Governance proof infrastructure...', delay: 1400 },
  { icon: Server, text: 'Control State Snapshot service ready · Temporal reconstruction enabled...', delay: 1800 },
  { icon: Server, text: 'Proof Graph Service ready · 47,832 nodes · governance lineage indexed...', delay: 2100 },
  { icon: Server, text: 'Initializing Decision Ledger [47,832 records]...', delay: 2400 },
  { icon: Shield, text: 'Initializing Governance Proof Chain · 5 workflows · 47,832 signed records...', delay: 2800 },
];

export default function SplashPage({ onStartTour }) {
  const navigate = useNavigate();
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [systemReady, setSystemReady] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const timers = [];
    connectionSteps.forEach((step, i) => {
      timers.push(setTimeout(() => setVisibleSteps(i + 1), step.delay));
    });
    timers.push(setTimeout(() => setSystemReady(true), 3000));
    timers.push(setTimeout(() => setShowButton(true), 3500));
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div
      data-testid="splash-page"
      className="min-h-screen bg-[#080D1A] flex items-center justify-center relative splash-bg overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          background: 'none',
        }}
      />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(99,102,241,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.3) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 text-center max-w-2xl mx-auto px-4 sm:px-6">
        {/* Logo */}
        <div className="animate-fade-in-up mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#6366F1]/10 border border-[#6366F1]/20 flex items-center justify-center animate-pulse-glow">
            <Shield size={40} className="text-[#6366F1]" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            APEX
          </h1>
          <p className="text-[#64748B] text-sm tracking-[0.25em] uppercase">
            Temporal AI Governance · Proof Infrastructure
          </p>
          <p className="text-[#475569] text-[11px] mt-3 tracking-wide">
            One workflow. One SDK wrapper. One proof chain. Seven days.
          </p>
        </div>

        {/* Terminal */}
        <div className="glass-card p-4 sm:p-6 text-left mb-8 animate-fade-in stagger-2" style={{ opacity: 0 }}>
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#1E2A45]">
            <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
            <span className="ml-2 text-[10px] font-mono text-[#475569]">apex-secure-connect</span>
          </div>
          <div className="space-y-2.5">
            {connectionSteps.map((step, i) => {
              const Icon = step.icon;
              const visible = i < visibleSteps;
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 text-sm font-mono transition-all duration-300 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                  }`}
                >
                  <Icon size={14} className="text-[#6366F1] flex-shrink-0" />
                  <span className="text-[#94A3B8] text-xs sm:text-sm">{step.text}</span>
                  {visible && (
                    <CheckCircle2 size={14} className="text-[#10B981] flex-shrink-0 ml-auto" />
                  )}
                </div>
              );
            })}
            {systemReady && (
              <div className="flex items-center gap-3 text-sm font-mono animate-fade-in pt-2 border-t border-[#1E2A45]">
                <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <span className="text-[#10B981] font-medium">
                  System integrity verified. All modules operational.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* CTA Buttons */}
        {showButton && (
          <div className="animate-fade-in-up flex flex-col items-center gap-3">
            <button
              data-testid="enter-platform-btn"
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-3 bg-[#6366F1] hover:bg-[#818cf8] text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-[0_0_30px_rgba(99,102,241,0.3)] hover:shadow-[0_0_50px_rgba(99,102,241,0.5)] hover:-translate-y-0.5 w-64 justify-center"
            >
              Enter Platform
              <ArrowRight size={18} />
            </button>
            <button
              data-testid="start-tour-btn"
              onClick={() => { navigate('/dashboard'); setTimeout(() => onStartTour && onStartTour(), 100); }}
              className="inline-flex items-center gap-3 border-2 border-white/60 hover:border-white text-white font-semibold px-8 py-3.5 rounded-xl transition-all hover:bg-white/5 w-64 justify-center"
            >
              See how it works
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
